import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

interface StatCardProps {
  end: number;
  label: string;
  suffix?: string;
  delay: number;
}

function StatCard({ end, label, suffix = '', delay }: StatCardProps) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = end / steps;
    let current = 0;

    const timer = setTimeout(() => {
      const counter = setInterval(() => {
        current += increment;
        if (current >= end) {
          setCount(end);
          clearInterval(counter);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(counter);
    }, delay);

    return () => clearTimeout(timer);
  }, [end, delay]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.9, delay: delay / 1000 }}
      className="glass-panel p-8 rounded-2xl glow-gold text-center smooth-transition hover-lift"
    >
      <div className="text-5xl md:text-6xl font-display font-bold gradient-text mb-2">
        {count}{suffix}
      </div>
      <div className="text-white/70 font-body text-sm">{label}</div>
    </motion.div>
  );
}

export default function LuxuryStatsSection() {
  return (
    <section className="relative py-24 bg-gradient-to-b from-charcoal to-deepBlue overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
            Experience & <span className="gradient-text">Achievements</span>
          </h2>
          <p className="text-white/70 text-lg font-body max-w-2xl mx-auto">
            Delivering premium design solutions with proven results
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard end={3} label="Years Experience" suffix="+" delay={0} />
          <StatCard end={80} label="Projects Completed" suffix="+" delay={100} />
          <StatCard end={40} label="Social Media Campaigns" suffix="+" delay={200} />
          <StatCard end={100} label="Client Satisfaction" suffix="%" delay={300} />
        </div>
      </div>
    </section>
  );
}
